// Rig Data Sheet — v2 storage layer.
//
// Storage key: rsc.rigsheets.v1  (deliberately separate from rsc.profiles.*).
//
// Versioning contract — same as v1 profile storage:
//   * Create → version = 1
//   * Update → version += 1, updatedAt = now()
//   * Clone  → new id, version = 1
//   * Delete → removes ALL versions of that id
//   * History → all prior versions retained for future audit
//
// Row shape: { id, version, createdAt, updatedAt, label, payload }.
// Payload shape: see /lib/rigsheet/schema.js.

import { emptyRigSheet, validateRigSheet } from "./schema";
import { recomputeAllPumps } from "./compute";
import { scoped } from "@/lib/accountScope";

export const RIGSHEET_STORAGE_KEY = "rsc.rigsheets.v1";

const _now = () => new Date().toISOString();

const _genId = () =>
  `rigsheet_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const _safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

function _readAll() {
  return _safeParse(scoped.getItem(RIGSHEET_STORAGE_KEY), []);
}
function _writeAll(rows) {
  scoped.setItem(RIGSHEET_STORAGE_KEY, JSON.stringify(rows));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Returns the latest version of each distinct Rig Data Sheet id. */
export function listRigSheets() {
  const all = _readAll();
  const latestById = new Map();
  for (const row of all) {
    const prev = latestById.get(row.id);
    if (!prev || row.version > prev.version) latestById.set(row.id, row);
  }
  return [...latestById.values()].sort(
    (a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""),
  );
}

/** Latest version of a single rig sheet by id, or null. */
export function getRigSheet(id) {
  const list = listRigSheets();
  return list.find((r) => r.id === id) || null;
}

/** A specific historical version of a rig sheet, or null. */
export function getByVersion(id, version) {
  const all = _readAll();
  return all.find((r) => r.id === id && r.version === version) || null;
}

/** All versions of a single id, newest first. Useful for an audit panel. */
export function getHistory(id) {
  const all = _readAll();
  return all.filter((r) => r.id === id).sort((a, b) => b.version - a.version);
}

/**
 * Create a new Rig Data Sheet. Validates first; throws on failure.
 * Re-runs pump derivations on save so stored `computed` values always
 * match the stored inputs (kill sheets read these directly).
 */
export function createRigSheet(payload) {
  const baked = recomputeAllPumps(payload);
  const result = validateRigSheet(baked);
  if (!result.ok) {
    const err = new Error(`Invalid Rig Data Sheet: ${result.errors.join(" ")}`);
    err.validationErrors = result.errors;
    throw err;
  }
  const all = _readAll();
  const row = {
    id:        _genId(),
    version:   1,
    createdAt: _now(),
    updatedAt: _now(),
    label:     String(baked.rigName || "").trim(),
    payload:   baked,
  };
  all.push(row);
  _writeAll(all);
  return row;
}

/**
 * Update an existing Rig Data Sheet. Bumps version; previous versions
 * remain in storage for audit / kill-sheet snapshot lookup.
 */
export function updateRigSheet(id, payload) {
  const baked = recomputeAllPumps(payload);
  const result = validateRigSheet(baked);
  if (!result.ok) {
    const err = new Error(`Invalid Rig Data Sheet: ${result.errors.join(" ")}`);
    err.validationErrors = result.errors;
    throw err;
  }
  const all = _readAll();
  const versions = all.filter((r) => r.id === id);
  if (versions.length === 0) throw new Error(`Rig Data Sheet not found: ${id}`);
  const nextVersion = versions.reduce((m, r) => Math.max(m, r.version), 0) + 1;
  const newRow = {
    id,
    version:   nextVersion,
    createdAt: versions[0].createdAt,
    updatedAt: _now(),
    label:     String(baked.rigName || "").trim(),
    payload:   baked,
  };
  all.push(newRow);
  _writeAll(all);
  return newRow;
}

/** Clone an existing sheet. New id, version=1. */
export function cloneRigSheet(id, newLabel) {
  const latest = getRigSheet(id);
  if (!latest) throw new Error(`Rig Data Sheet not found: ${id}`);
  const payload = {
    ...latest.payload,
    rigName: (newLabel || latest.payload.rigName || "Rig") + " (copy)",
  };
  return createRigSheet(payload);
}

/** Delete a rig sheet (removes ALL versions of that id). */
export function deleteRigSheet(id) {
  const all = _readAll();
  const filtered = all.filter((r) => r.id !== id);
  if (filtered.length === all.length) return false;
  _writeAll(filtered);
  return true;
}

// ---------------------------------------------------------------------------
// Read helpers designed for future Kill Sheet v2 consumption.
// Stable, explicit accessors so v2 worksheets never need to traverse
// dotted paths. If a sheet's defaults reference a missing pump, these
// fall back to the first pump in the list.
// ---------------------------------------------------------------------------

export function getActiveCirculatingPump(sheetOrPayload) {
  const p = _payloadOf(sheetOrPayload);
  if (!p || !Array.isArray(p.pumps) || p.pumps.length === 0) return null;
  const id = p.defaults?.defaultCirculatingPumpId;
  return p.pumps.find((x) => x.id === id) || p.pumps[0];
}

export function getActiveKillPump(sheetOrPayload) {
  const p = _payloadOf(sheetOrPayload);
  if (!p || !Array.isArray(p.pumps) || p.pumps.length === 0) return null;
  const id = p.defaults?.defaultKillPumpId;
  return p.pumps.find((x) => x.id === id) || p.pumps[0];
}

export function getWellControlVolumes(sheetOrPayload) {
  const p = _payloadOf(sheetOrPayload);
  if (!p) return null;
  return p.wellControlVolumes || null;
}

function _payloadOf(x) {
  if (!x) return null;
  // Accept either a stored row { payload } or a raw payload.
  return x.payload && typeof x.payload === "object" ? x.payload : x;
}

// ---------------------------------------------------------------------------
// Test/diagnostic helpers — never called from UI.
// ---------------------------------------------------------------------------
export function _clearAll() {
  scoped.removeItem(RIGSHEET_STORAGE_KEY);
}
export function _seed(rows) {
  _writeAll(rows);
}

/** Convenience: build a fresh empty rig sheet draft. */
export function newRigSheetDraft() {
  return emptyRigSheet();
}
